import { useMemo } from 'react'
import { ColDef, ValueFormatterParams } from 'ag-grid-community'
import { ExternalServiceApiTokenRow } from '../ExternalServiceApiTokenRow'
import { formatDate, formatDateTime } from '../../../../utils/date'
import { intl } from '../../../../i18n'
import { useRowNumber } from '../../../page-properties/bulksheet-properties/rowNumber'
import { colorPalette } from '../../../style/colorPalette'

const dateValueFormatter = (
  params: ValueFormatterParams<ExternalServiceApiTokenRow, number>
): string => {
  if (!params.value) return ''
  return formatDate(params.value) ?? ''
}

const dateTimeValueFormatter = (
  params: ValueFormatterParams<ExternalServiceApiTokenRow, number>
): string => {
  if (!params.value) return ''
  return formatDateTime(params.value) ?? ''
}

const scopesValueFormatter = (
  params: ValueFormatterParams<ExternalServiceApiTokenRow, string[]>
): string => {
  if (!params.value || params.value.length === 0) return ''
  return params.value.join(', ')
}

const getStatusText = (
  data: ExternalServiceApiTokenRow | undefined
): string => {
  if (!data) return ''
  if (data.revoked) {
    return intl.formatMessage({ id: 'externalServiceApiToken.status.revoked' })
  }
  if (data.expired) {
    return intl.formatMessage({ id: 'externalServiceApiToken.status.expired' })
  }
  return intl.formatMessage({ id: 'externalServiceApiToken.status.active' })
}

const getStatusColor = (
  data: ExternalServiceApiTokenRow | undefined
): string => {
  if (!data) return ''
  if (data.revoked) return colorPalette.monotone[4]
  if (data.expired) return colorPalette.yellow[3]
  return colorPalette.green[5]
}

export const useColumnDefs = (): ColDef<ExternalServiceApiTokenRow>[] => {
  const rowNumberColDef = useRowNumber()

  return useMemo(
    () => [
      {
        ...rowNumberColDef,
        pinned: 'left',
      },
      {
        field: 'serviceName',
        headerName: intl.formatMessage({
          id: 'externalServiceApiToken.serviceName',
        }),
        minWidth: 150,
      },
      {
        field: 'scopes',
        headerName: intl.formatMessage({
          id: 'externalServiceApiToken.scopes',
        }),
        width: 200,
        minWidth: 150,
        valueFormatter: scopesValueFormatter,
      },
      {
        field: 'expiredAt',
        headerName: intl.formatMessage({
          id: 'externalServiceApiToken.expiredAt',
        }),
        width: 120,
        valueFormatter: dateValueFormatter,
      },
      {
        field: 'createdAt',
        headerName: intl.formatMessage({
          id: 'externalServiceApiToken.createdAt',
        }),
        width: 160,
        valueFormatter: dateTimeValueFormatter,
      },
      {
        colId: 'status',
        headerName: intl.formatMessage({
          id: 'externalServiceApiToken.status',
        }),
        width: 120,
        valueGetter: params => getStatusText(params.data),
        cellStyle: params => ({
          color: getStatusColor(params.data),
          fontWeight: 'bold',
        }),
      },
    ],
    [rowNumberColDef]
  )
}
